// src/authpages/dashboard/Dashboard.js
import React, { useState, useEffect } from 'react';
import { getUserStudyStats } from './StudyStatsService';
import WeeklyActivityChart from './WeeklyActivityChart';
import FriendsList from './FriendsList';
import StudyGroup from '../group/StudyGroup';
import RewardsPage from './Rewards'; // Fixed import path to match the actual file name
import { auth } from '../../firebase';
import { Trophy } from 'lucide-react'; // Import the Trophy icon for the Rewards tab

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [studyStats, setStudyStats] = useState({
    totalStudyTime: 0,
    completedSessions: 0,
    weeklyAverage: 0,
    studyDays: {}
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch study stats from Firebase
  useEffect(() => {
    const fetchStats = async () => {
      try {
        console.log('Dashboard: Fetching study stats...');
        setLoading(true);
        
        // Check if user is authenticated
        if (!auth.currentUser) {
          console.error('Dashboard: No user is logged in');
          setError('Please log in to view your dashboard');
          setLoading(false);
          return;
        }
        
        const stats = await getUserStudyStats();
        console.log('Dashboard: Retrieved stats', stats);
        
        if (stats) {
          setStudyStats(stats);
        } else {
          console.warn('Dashboard: No stats returned from service');
          // Keep default empty stats
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Dashboard: Error fetching study stats:', error);
        setError('Failed to load dashboard data');
        setLoading(false);
      }
    };

    fetchStats();
    
    // Set up a refresh interval (every 5 minutes)
    const refreshInterval = setInterval(fetchStats, 300000);
    
    return () => clearInterval(refreshInterval);
  }, []);

  // Add this effect to listen for refresh events
useEffect(() => {
  // Function to handle refresh events
  const handleRefreshEvent = () => {
    console.log("Dashboard received refresh event");
    const fetchStats = async () => {
      try {
        console.log('Dashboard: Manual refresh triggered...');
        setLoading(true);
        
        if (!auth.currentUser) {
          console.error('Dashboard: No user is logged in');
          setError('Please log in to view your dashboard');
          setLoading(false);
          return;
        }
        
        const stats = await getUserStudyStats();
        console.log('Dashboard: Retrieved stats on refresh', stats);
        
        if (stats) {
          setStudyStats(stats);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Dashboard: Error fetching study stats:', error);
        setLoading(false);
      }
    };

    fetchStats();
  };

  // Add event listener for the refresh event
  window.addEventListener('refreshStudyStats', handleRefreshEvent);
  
  // Check localStorage directly on component mount
  const sessionCompleted = localStorage.getItem('studySessionCompleted');
  if (sessionCompleted === 'true') {
    console.log("Dashboard detected completed session flag");
    handleRefreshEvent();
  }
  
  // Clean up
  return () => {
    window.removeEventListener('refreshStudyStats', handleRefreshEvent);
  };
}, []);

  // Function to count active days (days with study activity)
  const countActiveDays = () => {
    if (!studyStats.studyDays) return 0;
    return Object.entries(studyStats.studyDays)
      .filter(([date, minutes]) => minutes > 0)
      .length;
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return (
          <>
            {/* Stats Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-white shadow-sm rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Study Sessions</h3>
                <p className="text-3xl font-bold text-blue-600">
                  {studyStats?.completedSessions || 0}
                </p>
                <p className="text-sm text-gray-500 mt-1">Completed sessions</p>
              </div>
              
              <div className="bg-white shadow-sm rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Study Time</h3>
                <p className="text-3xl font-bold text-green-600">
                  {studyStats?.totalStudyTime ? Math.round(studyStats.totalStudyTime / 60) : 0}
                </p>
                <p className="text-sm text-gray-500 mt-1">Total hours studied</p>
              </div>
              
              <div className="bg-white shadow-sm rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Weekly Average</h3>
                <p className="text-3xl font-bold text-purple-600">
                  {studyStats?.weeklyAverage || 0}
                </p>
                <p className="text-sm text-gray-500 mt-1">Minutes per day</p>
              </div>
              
              <div className="bg-white shadow-sm rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Active Days</h3>
                <p className="text-3xl font-bold text-orange-600">
                  {countActiveDays()}
                </p>
                <p className="text-sm text-gray-500 mt-1">Days with activity</p>
              </div>
            </div>
            
            {/* Weekly Activity Chart */}
            <WeeklyActivityChart />
          </>
        );
      
      case 'rewards':
        // Return the new Rewards component
        return <RewardsPage />;
      
      case 'friends':
        return <FriendsList />;
      
      case 'studyGroups':
        return <StudyGroup />;
      
      default:
        return <div>Select a tab</div>;
    }
  };

  if (loading) {
    return <div className="text-center p-10">Loading dashboard data...</div>;
  }

  if (error) {
    return (
      <div className="text-center p-10">
        <p className="text-red-500">{error}</p>
        <button 
          onClick={() => window.location.reload()}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          Welcome, {auth.currentUser?.displayName || 'Student'}!
        </h1>
        <p className="text-gray-600">Here's what's happening with your studies</p>
      </div>
      
      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8">
          <button
            className={`px-1 py-4 text-sm font-medium border-b-2 ${
              activeTab === 'dashboard'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('dashboard')}
          >
            <span className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path>
              </svg>
              Dashboard
            </span>
          </button>
          
          <button
            className={`px-1 py-4 text-sm font-medium border-b-2 ${
              activeTab === 'rewards'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('rewards')}
          >
            <span className="flex items-center">
              <Trophy className="w-5 h-5 mr-2" />
              Rewards
            </span>
          </button>
          
          <button
            className={`px-1 py-4 text-sm font-medium border-b-2 ${
              activeTab === 'friends'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('friends')}
          >
            <span className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"></path>
              </svg>
              Friends
            </span>
          </button>
        </nav>
      </div>
      
      {/* Main Content Area - Tab Content */}
      <div className="mt-4">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default Dashboard;